import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tabs,
  Tab,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Schedule as ScheduleIcon,
  Delete as DeleteIcon,
  LinkedIn as LinkedInIcon,
  Twitter as TwitterIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

interface Post {
  id: string;
  platform: string;
  content: string;
  scheduled_time: string;
  status: string;
  engagement_count?: number;
  media_urls?: string[];
  created_at: string;
}

const platformIcons: Record<string, React.ReactNode> = {
  linkedin: <LinkedInIcon />,
  twitter: <TwitterIcon />,
  facebook: <FacebookIcon />,
  instagram: <InstagramIcon />,
};

export default function Posts() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDialog, setShowDialog] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [formData, setFormData] = useState({
    platform: 'linkedin',
    content: '',
    scheduled_time: '',
    media_urls: '',
  });

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/posts`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      setPosts(response.data || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const handleSchedulePost = async () => {
    try {
      await axios.post(
        `${API_URL}/posts/schedule`,
        {
          platform: formData.platform,
          content: formData.content,
          scheduled_time: formData.scheduled_time,
          media_urls: formData.media_urls ? formData.media_urls.split(',').map(s => s.trim()) : [],
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        }
      );

      setShowDialog(false);
      setFormData({ platform: 'linkedin', content: '', scheduled_time: '', media_urls: '' });
      loadPosts();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to schedule post');
    }
  };

  const handleDeletePost = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      await axios.delete(`${API_URL}/posts/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      loadPosts();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete post');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'published':
        return 'success';
      case 'scheduled':
        return 'info';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const filteredPosts = posts.filter((post) => {
    if (tabValue === 0) return post.status === 'scheduled';
    if (tabValue === 1) return post.status === 'published';
    return post.status === 'failed';
  });

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', sm: 'center' },
        mb: { xs: 2, sm: 3 },
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 2, sm: 0 },
        width: '100%'
      }}>
        <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Social Media Posts
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowDialog(true)}
          fullWidth={window.innerWidth < 600}
        >
          Schedule Post
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
            <Tab label="Scheduled" />
            <Tab label="Published" />
            <Tab label="Failed" />
          </Tabs>
        </CardContent>
      </Card>

      <TableContainer component={Paper} sx={{ 
        overflowX: 'auto',
        '& .MuiTable-root': {
          minWidth: { xs: 300, sm: 650 },
        },
        '& .MuiTableCell-root': {
          fontSize: { xs: '0.75rem', sm: '0.875rem' },
          padding: { xs: '8px', sm: '16px' },
        }
      }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Platform</TableCell>
              <TableCell>Content</TableCell>
              <TableCell>Scheduled Time</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Engagement</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPosts.map((post) => (
              <TableRow key={post.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {platformIcons[post.platform]}
                    <Typography variant="body2" sx={{ ml: 1, textTransform: 'capitalize' }}>
                      {post.platform}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                    {post.content}
                  </Typography>
                </TableCell>
                <TableCell>
                  {new Date(post.scheduled_time).toLocaleString()}
                </TableCell>
                <TableCell>
                  <Chip
                    label={post.status}
                    color={getStatusColor(post.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {post.engagement_count !== undefined ? post.engagement_count : '-'}
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleDeletePost(post.id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {filteredPosts.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No posts found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Schedule Post Dialog */}
      <Dialog open={showDialog} onClose={() => setShowDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Schedule Social Media Post</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Platform</InputLabel>
              <Select
                value={formData.platform}
                label="Platform"
                onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
              >
                <MenuItem value="linkedin">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <LinkedInIcon sx={{ mr: 1 }} /> LinkedIn
                  </Box>
                </MenuItem>
                <MenuItem value="twitter">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <TwitterIcon sx={{ mr: 1 }} /> Twitter
                  </Box>
                </MenuItem>
                <MenuItem value="facebook">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <FacebookIcon sx={{ mr: 1 }} /> Facebook
                  </Box>
                </MenuItem>
                <MenuItem value="instagram">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <InstagramIcon sx={{ mr: 1 }} /> Instagram
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Post Content"
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              margin="normal"
              multiline
              rows={6}
              required
              helperText={`${formData.content.length} characters`}
            />

            <TextField
              fullWidth
              label="Scheduled Time"
              type="datetime-local"
              value={formData.scheduled_time}
              onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })}
              margin="normal"
              required
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              fullWidth
              label="Media URLs (Optional)"
              value={formData.media_urls}
              onChange={(e) => setFormData({ ...formData, media_urls: e.target.value })}
              margin="normal"
              helperText="Comma-separated URLs for images/videos"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSchedulePost}
            disabled={!formData.content || !formData.scheduled_time}
          >
            Schedule Post
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
